// Package E — Excel export using the already-installed exceljs (no new
// library). Consumes the EXACT same categories/summaryRows the UI and
// PDF export already use — zero independent calculation. Three
// sheets, matching the approved workbook structure exactly.
import ExcelJS from 'exceljs'
import { supabase } from '../../lib/supabase'

export async function exportTakeoffReportExcel({ categories, summaryRows, tenderName }) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'CostSpace Estimation'
  wb.created = new Date()

  // ---- Sheet 1: TAKEOFF SUMMARY ----
  const summarySheet = wb.addWorksheet('Takeoff Summary')
  summarySheet.columns = [
    { header: 'Category', key: 'category', width: 30 },
    { header: 'Unit', key: 'unit', width: 12 },
    { header: 'Total Quantity', key: 'total', width: 18 },
  ]
  summarySheet.getRow(1).font = { bold: true }
  for (const row of summaryRows) {
    summarySheet.addRow({ category: row.categoryName, unit: row.unit, total: row.total })
  }
  summarySheet.getColumn('total').numFmt = '#,##0.00'

  // ---- Sheet 2: DETAILED BREAKDOWN ----
  const detailSheet = wb.addWorksheet('Detailed Breakdown')
  detailSheet.columns = [
    { header: 'Category', key: 'category', width: 22 },
    { header: 'Takeoff Item', key: 'item', width: 24 },
    { header: 'Drawing', key: 'drawing', width: 18 },
    { header: 'Revision', key: 'revision', width: 12 },
    { header: 'Sheet', key: 'sheet', width: 12 },
    { header: 'Quantity', key: 'quantity', width: 16 },
    { header: 'Unit', key: 'unit', width: 10 },
  ]
  detailSheet.getRow(1).font = { bold: true }
  for (const cat of categories) {
    for (const item of cat.items) {
      for (const s of item.sheets) {
        detailSheet.addRow({
          category: cat.name, item: item.description, drawing: s.drawingName || '-',
          revision: s.revisionLabel || '-', sheet: s.sheetNumber, quantity: s.quantity, unit: item.unit,
        })
      }
    }
  }
  detailSheet.getColumn('quantity').numFmt = '#,##0.00'

  // ---- Sheet 3: MEASUREMENTS ----
  // Fetched fresh at export time (a single explicit query, not lazy
  // per-row like the UI's on-demand drilldown) — this is the ONE place
  // it makes sense to load full measurement detail eagerly, since the
  // user explicitly asked for a complete exported report. Uses the
  // SAME is_active filter and the SAME sheet_id set already resolved
  // via categories/summaryRows — no independent recalculation.
  const allSheetIds = [...new Set(categories.flatMap((cat) => cat.items.flatMap((item) => item.sheets.map((s) => s.sheetId))))]
  if (allSheetIds.length > 0) {
    const { data: measurements } = await supabase
      .from('measurement')
      .select('id, sheet_id, takeoff_item_id, measurement_type, calculated_value, polarity, is_active')
      .in('sheet_id', allSheetIds)
      .eq('is_active', true)

    const measurementSheet = wb.addWorksheet('Measurements')
    measurementSheet.columns = [
      { header: 'Category', key: 'category', width: 22 },
      { header: 'Item', key: 'item', width: 24 },
      { header: 'Drawing', key: 'drawing', width: 18 },
      { header: 'Revision', key: 'revision', width: 12 },
      { header: 'Sheet', key: 'sheet', width: 12 },
      { header: 'Measurement', key: 'measurement', width: 16 },
      { header: 'Polarity', key: 'polarity', width: 10 },
      { header: 'Quantity', key: 'quantity', width: 16 },
      { header: 'Unit', key: 'unit', width: 10 },
    ]
    measurementSheet.getRow(1).font = { bold: true }

    // Index sheet-level metadata (drawing/revision/sheet number, unit,
    // category, item description) by sheetId+itemId, built from the
    // SAME categories structure already on screen — this is a lookup,
    // not a recalculation.
    const sheetMetaByKey = new Map()
    for (const cat of categories) {
      for (const item of cat.items) {
        for (const s of item.sheets) {
          sheetMetaByKey.set(`${item.id}::${s.sheetId}`, { categoryName: cat.name, itemDescription: item.description, unit: item.unit, drawingName: s.drawingName, revisionLabel: s.revisionLabel, sheetNumber: s.sheetNumber })
        }
      }
    }

    let idx = 0
    for (const m of measurements || []) {
      const meta = sheetMetaByKey.get(`${m.takeoff_item_id}::${m.sheet_id}`)
      if (!meta) continue // measurement belongs to a sheet/item not in the current cross-drawing-latest scope (e.g. a superseded revision) — correctly excluded, matching the UI/PDF scope exactly.
      idx++
      const signedQty = m.polarity === 'negative' ? -Math.abs(Number(m.calculated_value)) : Math.abs(Number(m.calculated_value))
      measurementSheet.addRow({
        category: meta.categoryName, item: meta.itemDescription, drawing: meta.drawingName, revision: meta.revisionLabel,
        sheet: meta.sheetNumber, measurement: `Measurement ${String(idx).padStart(3, '0')}`, polarity: m.polarity, quantity: signedQty, unit: meta.unit,
      })
    }
    measurementSheet.getColumn('quantity').numFmt = '#,##0.00'
  }

  return wb
}
